import { useState } from "react";
import { Modal } from "../shared/Modal";
import { SearchSelect } from "../waba/MetricsTab";
import { ghostBtn, labelStyle, primaryBtn, shiftDay, todaySp, useTaskOptions } from "./common";

const FIELDS: [string, string, string][] = [
  ["agent_id", "obrigatório", "UUID do agente (o mesmo da URL do painel: /agents/{id})."],
  ["titulo", "obrigatório", "Até 200 caracteres. Vira o título do card."],
  ["descricao", "opcional", "Markdown, até 20 mil caracteres."],
  ["assuntos", "opcional", "Lista de tags (até 10). Assunto novo é criado; \"bug\" reaproveita \"Bug\"."],
  ["prazo", "opcional", "YYYY-MM-DD, horário de SP. Padrão: hoje. Não aceita data passada."],
  ["hora", "opcional", "HH:MM. Com hora, a tarefa fica vermelha quando o horário passa."],
  ["sistema", "opcional", "Até 60 caracteres. Badge vira \"API - {sistema}\"; sem ele, \"API\"."],
];

const preStyle: React.CSSProperties = { margin: 0, padding: 14, background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 8, fontSize: 12, lineHeight: 1.55, color: "var(--ink)", whiteSpace: "pre-wrap", wordBreak: "break-all" };

// Popup só p/ Admin: criar e consultar tarefas pela API pública, com curl pronto pra copiar.
export function ApiHelpModal({ onClose }: { onClose: () => void }) {
  const { data: opts } = useTaskOptions();
  const [agentId, setAgentId] = useState("");
  const [copied, setCopied] = useState<"post" | "get" | null>(null);
  const body = {
    agent_id: agentId || "UUID_DO_AGENTE",
    titulo: "Revisar prompt de follow-up",
    descricao: "Lead reclamou de mensagem repetida.\n\n- conversa: https://...",
    assuntos: ["Revisão", "Ajuste de bug"],
    prazo: shiftDay(todaySp(), 1),
    hora: "14:00",
    sistema: "Análise de erros",
  };
  const curl = [
    `curl -X POST '${window.location.origin}/api/tasks/public' \\`,
    `  -H 'Content-Type: application/json' \\`,
    `  -H 'x-api-key: SUA_CHAVE' \\`,
    `  -d '${JSON.stringify(body).replace(/'/g, "'\\''")}'`,
  ].join("\n");
  const curlGet = [
    `curl '${window.location.origin}/api/tasks/public?agent_id=${agentId || "UUID_DO_AGENTE"}' \\`,
    `  -H 'x-api-key: SUA_CHAVE'`,
  ].join("\n");

  async function copy(text: string, which: "post" | "get") {
    await navigator.clipboard.writeText(text);
    setCopied(which);
    setTimeout(() => setCopied(null), 1500);
  }

  return (
    <Modal onClose={onClose} zIndex={1000}>
      <div style={{ width: 760, maxWidth: "100%", maxHeight: "calc(100vh - 48px)", overflowY: "auto", background: "var(--surface-card)", border: "1px solid var(--line-strong)", borderRadius: 12, boxShadow: "var(--shadow-modal)", padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: "var(--ink)", flex: 1 }}>API de tarefas</h2>
          <button onClick={onClose} aria-label="Fechar" style={{ background: "none", border: "none", color: "var(--ink-muted)", fontSize: 22, lineHeight: 1, cursor: "pointer" }}>×</button>
        </div>

        <div style={{ fontSize: 13, color: "var(--ink-secondary)", lineHeight: 1.6 }}>
          Sistemas externos criam tarefas com <code>POST /api/tasks/public</code>. A autenticação é o header{" "}
          <code>x-api-key</code> com uma chave cadastrada em <b>Configurações → APIs</b> com provider{" "}
          <code>tarefas</code> (crie uma por sistema). A tarefa cai no quadro do dono do agente, com a badge{" "}
          <b>API</b> ou <b>API - sistema</b>. Anexos só pelo painel.
        </div>

        <div>
          <span style={labelStyle}>Campos do body (JSON)</span>
          <div style={{ border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden" }}>
            {FIELDS.map(([name, req, desc], i) => (
              <div key={name} style={{ display: "grid", gridTemplateColumns: "110px 90px 1fr", gap: 10, padding: "8px 12px", fontSize: 12.5, borderTop: i ? "1px solid var(--line)" : undefined }}>
                <code style={{ color: "var(--ink)" }}>{name}</code>
                <span style={{ color: req === "obrigatório" ? "var(--brand)" : "var(--ink-muted)" }}>{req}</span>
                <span style={{ color: "var(--ink-secondary)" }}>{desc}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ fontSize: 12.5, color: "var(--ink-secondary)", lineHeight: 1.6 }}>
          <b>Respostas:</b> 201 <code>{"{id, prazo, hora}"}</code> · 401 chave inválida · 404 agente não encontrado · 422 validação (ex.: prazo no passado).
        </div>

        <div>
          <span style={labelStyle}>Exemplo — criar</span>
          <div style={{ marginBottom: 8 }}>
            <SearchSelect value={agentId} onChange={setAgentId} options={(opts?.agents ?? []).map(a => ({ value: a.id, label: a.name }))} placeholder="Preencher agent_id com um agente…" />
          </div>
          <pre style={preStyle}>{curl}</pre>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
            <button onClick={() => copy(curl, "post")} style={primaryBtn}>{copied === "post" ? "Copiado!" : "Copiar curl"}</button>
          </div>
        </div>

        <div>
          <span style={labelStyle}>Consultar — espelho das tarefas do agente</span>
          <div style={{ fontSize: 13, color: "var(--ink-secondary)", lineHeight: 1.6, marginBottom: 8 }}>
            <code>GET /api/tasks/public?agent_id=</code> com a mesma chave devolve <b>todas</b> as tarefas do agente
            (abertas e finalizadas), completas: estado atual, <code>prazo_original</code>, <code>mudancas_prazo</code>{" "}
            (de → para, motivo, quem, quando), finalização (<code>finalizada_por</code>, <code>resolucao</code>,{" "}
            <code>gestor_na_finalizacao</code>) e <code>anexos</code> com <code>url</code> assinada que vale 1h
            (<code>url_expira_em</code>) — baixe na hora. Tarefa excluída pelo Admin some da lista: o espelho remove os ids ausentes.
          </div>
          <pre style={preStyle}>{curlGet}</pre>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
            <button onClick={onClose} style={ghostBtn}>Fechar</button>
            <button onClick={() => copy(curlGet, "get")} style={primaryBtn}>{copied === "get" ? "Copiado!" : "Copiar curl"}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
